<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<template>
  <div class="flexsearch-data-table">
    <v-snackbar
      top
      :timeout="10000"
      :color="toasterMessage.mode"
      :value="toasterMessage.text"
      ref="testSnackBar"
    >
      <span v-html="toasterMessage.text" />

      <a @click.stop="clearMessage"> Close </a>
    </v-snackbar>
    <div class="confirmations">
      <v-dialog
        class="trash-confirmation"
        v-model="showResubmitBatch"
        :persistent="true"
      >
        <div class="orange-top" />
        <p>Are you sure you want to resubmit this batch?</p>
        <div class="action-links-dialog">
          <a class="alignleft-links" @click.stop="showResubmitBatch = false"
            >Cancel</a
          >
          <a class="alignright-links" @click.stop="resumbitBatch(holdBatchId)"
            >Yes, resubmit batch</a
          >
        </div>
      </v-dialog>
      <v-dialog
        class="trash-confirmation"
        v-model="showTrashBatch"
        :persistent="true"
      >
        <div class="orange-top" />
        <p>Are you sure you want to remove this batch?</p>
        <div class="action-links-dialog">
          <a class="alignleft-links" @click.stop="showTrashBatch = false"
            >Cancel</a
          >
          <a class="alignright-links" @click.stop="trashBatch(holdBatchId)"
            >Yes, remove batch</a
          >
        </div>
      </v-dialog>
      <v-dialog
        class="trash-confirmation"
        v-model="showKillBatch"
        :persistent="true"
      >
        <div class="orange-top" />
        <p>Are you sure you want to cancel this batch?</p>
        <div class="action-links-dialog">
          <a class="alignleft-links" @click.stop="showKillBatch = false"
            >Cancel</a
          >
          <a class="alignright-links" @click.stop="killBatch(holdBatchId)"
            >Yes, cancel batch</a
          >
        </div>
      </v-dialog>
    </div>
    <div class="filtering-options">
      <v-form class="sort-options">
        <div class="sorting-field">
          <label>Search</label>
          <v-text-field solo v-model="fileNameFilter" @input="search()" />
        </div>
        <div class="sorting-field" v-if="isSystemAdmin()">
          <label>Client</label>
          <v-select
            solo
            :items="clients"
            v-model="clientFilter"
            @input="updateFiltersData"
          >
            <template slot="selection" slot-scope="data">
              {{ data.item.displayName }}
            </template>
            <template slot="item" slot-scope="data">
              {{ data.item.displayName }}
            </template>
          </v-select>
        </div>
        <div class="sorting-field" v-if="isTeamOwner()">
          <label>Team</label>
          <v-select
            solo
            v-model="teamFilter"
            :items="teams"
            @input="updateFiltersData"
          >
            <template slot="selection" slot-scope="data">
              {{ data.item.displayName }}
            </template>
            <template slot="item" slot-scope="data">
              {{ data.item.displayName }}
            </template>
          </v-select>
        </div>
        <div class="sorting-field">
          <label>Uploaded by</label>
          <v-select
            solo
            v-model="uploaderFilter"
            :items="uploaders"
            @input="updateFiltersData"
          >
            <template slot="selection" slot-scope="data">
              {{ data.item.displayName }}
            </template>
            <template slot="item" slot-scope="data">
              {{ data.item.displayName }}
            </template>
          </v-select>
        </div>
        <div class="sorting-field">
          <v-btn @click.stop="resetFilters">Reset</v-btn>
        </div>
      </v-form>
    </div>
    <v-data-table
      :headers="batchHistoryHeaders"
      :items="batchHistories"
      :pagination.sync="pagination"
      :hide-actions="true"
    >
      <template slot="items" slot-scope="props">
        <td
          class="first-column"
          :style="{
            backgroundColor:
              props.item.hits == 0 &&
              props.item.recordCount != 0 &&
              checkExtStatusAnd(props.item.status)
                ? '#24A555'
                : '#FCBE32'
          }"
        />
        <td class="second-column">{{ props.item.fileName }}</td>
        <td>{{ props.item.processedTimeStamp | toDate }}</td>
        <td>{{ props.item.uploadedBy }}</td>
        <td>{{ props.item.recordCount }}</td>
        <td>{{ props.item.hits }}</td>
        <td>
          <v-icon
            v-if="
              props.item.hits == 0 &&
                props.item.recordCount != 0 &&
                checkExtStatusAnd(props.item.status)
            "
            class="check-circle"
            >fa fa-check-circle-o
          </v-icon>
          <router-link
            v-if="checkExtStatusAnd(props.item.status)"
            :to="`/flex-search/${props.item.batchId}/details`"
          >
            {{
              status(props.item.recordCount, props.item.hits, props.item.status)
            }}
          </router-link>
          <p v-else>
            {{
              status(props.item.recordCount, props.item.hits, props.item.status)
            }}
          </p>
        </td>
        <td>
          <div
            @click.stop="$set(showBatchOptions, props.index, true)"
            @mouseleave="$set(showBatchOptions, props.index, false)"
          >
            <v-btn icon>
              <v-icon class="ellipsis">fa fa-ellipsis-h </v-icon>
            </v-btn>
            <div
              class="ellipsis-menu"
              v-show="showBatchOptions[props.index]"
              @click.stop="$set(showBatchOptions, props.index, false)"
            >
              <p>Batch Actions</p>
              <ul>
                <li v-if="checkStatusesAnd(props.item.status)">
                  <a
                    class="batch-options"
                    :href="getDownloadUrl(props.item.batchId)"
                    >Download File</a
                  >
                </li>
                <li>
                  <a
                    class="batch-options"
                    @click="
                      showResubmitBatch = true
                      holdBatchId = props.item.batchId
                    "
                    >Resubmit</a
                  >
                </li>
                <li v-if="checkStatusesOr(props.item.status)">
                  <a
                    class="batch-options"
                    ref="cancel-batch"
                    @click="
                      showKillBatch = true
                      holdBatchId = props.item.batchId
                    "
                    >Cancel Batch</a
                  >
                </li>
                <li v-if="isSystemAdmin()">
                  <a
                    class="batch-options"
                    @click="
                      showTrashBatch = true
                      holdBatchId = props.item.batchId
                    "
                    >Remove Batch</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>
    <div class="text-xs-center pt-2">
      <v-pagination
        v-model="pagination.page"
        :length="this.pagination.total + 1"
        :total-visible="7"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios"

import {
  API,
  batchStatuses,
  batchStatusesExt,
  SEARCH_DEBOUNCE_TIMEOUT
} from "@/common/constants"
import moment from "moment"
import { debounce } from "lodash"
import { mapGetters } from "vuex"

import toasterMixin from "@/mixins/toaster"

const thePromise = Promise.resolve()

let timeoutId

export default {
  name: "FlexSearchDataTable",
  mixins: [toasterMixin],

  data: () => ({
    otherSearchTypes: [],

    batchHistories: [],

    pagination: {
      total: 0,
      rowsPerPage: 25
    },

    showBatchOptions: [],

    showTrashBatch: false,

    showKillBatch: false,

    showResubmitBatch: false,

    holdBatchId: -1,

    fileNameFilter: undefined,

    clients: [],

    clientFilter: "",

    teams: [],

    teamFilter: "",

    uploaders: [],

    uploaderFilter: ""
  }),

  methods: {
    ...mapGetters(["isSystemAdmin", "isTeamOwner"]),

    search: debounce(function() {
      thePromise.then(this.listBatchHistory())
    }, SEARCH_DEBOUNCE_TIMEOUT),

    resetFilters() {
      this.clientFilter = {}
      this.teamFilter = {}
      this.uploaderFilter = {}
      this.fileNameFilter = ""

      this.updateFiltersData()
    },

    updateFiltersData() {
      axios
        .get(API.FLEXSEARCH_FILTER_ENDPOINT, {
          params: {
            clientFilter: this.clientFilter.id,
            teamFilter: this.teamFilter.id
          }
        })
        .then(response => {
          this.clients = response.data.clients
          this.teams = response.data.teams
          this.uploaders = response.data.uploaders
        })

      this.listBatchHistory()
    },

    listBatchHistory() {
      axios
        .get(API.LIST_ENDPOINT, {
          params: {
            fileNameFilter: this.fileNameFilter,
            clientFilter: this.clientFilter.id,
            teamFilter: this.teamFilter.id,
            uploaderFilter: this.uploaderFilter.id
          }
        })
        .then(response => {
          this.batchHistories = response.data
          this.pagination.total = Math.floor(
            (response.data.length - 1) / this.pagination.rowsPerPage
          )
        })
    },

    status(recordCount, hits, status) {
      var newStatus = ""
      if (recordCount === 0 || status === "Processing") {
        newStatus = status
      } else if (hits === 0) {
        newStatus = "All Results Reviewed"
      } else {
        newStatus = "Results to Review"
      }

      return newStatus
    },

    killBatch(id) {
      axios.post(API.FLEXSEARCH_KILL_ENDPOINT.replace(":batchId", id), {}).then(
        () => {
          this.listBatchHistory()
          this.showKillBatch = false
        },
        () => {
          this.showMessage(`Failed to cancel batch search.`, "error")
          this.showKillBatch = false
        }
      )
    },

    trashBatch(id) {
      axios
        .post(API.FLEXSEARCH_TRASH_ENDPOINT.replace(":batchId", id), {})
        .then(
          () => {
            this.listBatchHistory()
            this.showTrashBatch = false
          },
          () => {
            this.showMessage(`Failed to trash batch search.`, "error")
            this.showKillBatch = false
          }
        )
    },

    resumbitBatch(id) {
      axios
        .get(API.FLEXSEARCH_RESUBMIT_ENDPOINT.replace(":batchId", id), {})
        .then(
          response => {
            this.showMessage(response.data, "success")
            this.listBatchHistory()
            this.showResubmitBatch = false
          },
          error => {
            this.showMessage(error.response.data, "error")
            this.showResubmitBatch = false
          }
        )
    },

    getDownloadUrl(id) {
      return API.GETFILE_ENDPOINT.replace(":batchId", id)
    },
    checkExtStatusAnd(status) {
      return batchStatusesExt.indexOf(status) === -1
    },
    checkStatusesAnd(status) {
      return batchStatuses.indexOf(status) === -1
    },
    checkStatusesOr(status) {
      return batchStatuses.indexOf(status) !== -1
    }
  },

  computed: {
    batchHistoryHeaders() {
      return [
        "",
        "File Name",
        "Date Uploaded",
        "Uploaded By",
        "Record Count",
        "To Review",
        "Status",
        "action"
      ].map(name => ({ text: name, sortable: false }))
    }
  },
  created() {
    this.listBatchHistory()

    this.updateFiltersData()

    const repeatListBatchHistory = () => {
      timeoutId = window.setTimeout(repeatListBatchHistory, 5000)
      this.listBatchHistory()
    }

    repeatListBatchHistory()
  },

  beforeDestroy() {
    window.clearTimeout(timeoutId)
  },

  filters: {
    toDate(timestamp) {
      return moment.utc(timestamp).format("YYYY-MM-DD")
    }
  }
}
</script>

<style lang="scss" scoped>
.flexsearch-data-table {
  padding-top: 0 !important;

  .filtering-options {
    /deep/ .sort-options {
      display: flex;
      align-items: flex-end;
      padding-top: 2.25rem;
      padding-bottom: 0.75rem;
      font-weight: $heavy;
      text-align: left;

      .v-input__control {
        margin-top: 0.375rem;
        min-width: 10rem;
        color: #4a4a4a;
        font-weight: $normal;
        line-height: 1.125rem;
        background-color: #ffffff;
        border-radius: 0.125rem;
        max-width: 18rem;
        height: 1.875rem;
        min-height: 0;
        .v-input__slot {
          margin: 0;
          padding-right: 0.3rem;
        }
      }

      label {
        color: $color-ec-black1;
        padding-bottom: 0.43rem;
      }

      .sorting-field {
        display: inline-block;
        padding-right: 0.75rem;

        .v-btn {
          background-color: $color-ec-btn-gray;
          border-radius: 0.125rem;
          box-shadow: 0 0.0625rem 0.25rem 0 rgba(0, 0, 0, 0.23999999463558197),
            0 0.0625rem 0.0625rem 0 rgba(0, 0, 0, 0.11999999731779099);
          width: 3.75rem;
          height: 1.875rem;
          color: $color-white;
          font-size: 0.875rem;
          margin: 0;
        }
      }
    }
  }

  /deep/ .v-table {
    /* the table headers */
    thead tr {
      height: 2rem;
      background-color: $color-ec-blue;

      th {
        color: $color-white;
        font-size: 1rem;
        font-weight: $normal;
        text-align: left;
        padding-right: 0.375rem !important;
        padding-left: 0.375rem !important;
        height: 2rem;
      }

      th:last-child {
        color: $color-ec-blue;
        width: 2.5rem !important;
        padding-right: 0.1875rem !important;
        padding-left: 0.1875rem !important;
      }

      th:first-child {
        padding-right: 0.1875rem !important;
        padding-left: 0.1875rem !important;
      }
    }
    /* the data rows */
    tbody {
      tr:nth-child(even) {
        background-color: $color-off-white;
      }

      tr td {
        color: $color-ec-black1;
        font-size: 0.875rem;
        font-weight: $normal;
        text-align: left;
        padding-right: 0.375rem !important;
        padding-left: 0.375rem !important;

        a {
          text-decoration: underline;
        }

        p {
          margin-bottom: 0;
        }
      }

      tr td:first-child {
        padding-right: 0.1875rem !important;
        padding-left: 0.1875rem !important;
      }

      tr td:last-child {
        padding-right: 0.1875rem !important;
        padding-left: 0.1875rem !important;
      }
    }
  }
}

.first-column {
  width: 0.375rem !important;
}

.check-circle {
  color: #24a555;
  font-size: 0.875rem;
  font-weight: $normal;
  text-align: left;
  padding-right: 0.3rem !important;
  padding-bottom: 0.1rem !important;
}

.ellipsis {
  color: $color-ec-blue !important;
  font-size: 1.125rem;
  font-weight: $normal;
  text-align: left;
}

.ellipsis-menu {
  background-color: $color-white;
  border: 0.0625rem solid $color-ec-blue;
  border-radius: 0.125rem;
  width: 10.4rem;
  max-height: 14rem;
  z-index: 20;
  position: absolute;

  li {
    padding: 0.5rem 0;
  }

  a {
    text-decoration: underline;
    padding-left: 0.75rem;
  }

  p {
    color: $color-ec-black1;
    font-size: 0.875rem;
    font-weight: $heavy;
    line-height: 1rem;
    padding: 0.75rem;
  }
}

/deep/ .v-dialog {
  background-color: $color-white;
  width: auto;
  height: auto;
  text-align: center;

  p {
    font-size: 1rem;
    font-weight: $heavy;
    padding-left: 5rem;
    padding-right: 5rem;
    padding-top: 3rem;
  }
  .action-links-dialog {
    position: flex;
    padding: 2rem 1.75rem 3rem;

    .alignleft-links {
      float: left;
      color: #aa0000;
      text-decoration: underline;
    }

    .alignright-links {
      float: right;
      text-decoration: underline;
    }
  }
}

.orange-top {
  @include orange-top;
}
</style>



// WEBPACK FOOTER //
// src/components/FlexSearch-DataTable.vue